import React from 'react';
import styled, { css } from 'styled-components';
import { Box } from '@rsos/base-components';
import MapCardHeader from '@rsos/components/capstone/common/Map/MapCardHeader';
import { StyledCard } from '../../AdditionalData/EllingtonCard/CustomCard.styles';
import { EllingtonCard } from '../../AdditionalData/EllingtonCard/EllingtonCard';
import Location from './Location';

const MAP_CARD_WIDTH = 275; // design calls for 305, subtracted 30px padding
const MAP_CARD_WIDTH_COLLAPSED = 180;
const MAP_CARD_HEIGHT_COLLAPSED = 30;

export const Scrollable = styled.div`
  padding: 16px;
  overflow-y: auto;
  max-height: 100%;
`;

export const StyledMapCard = styled(Box)`
  width: ${({ collapsed = false }) =>
    collapsed ? MAP_CARD_WIDTH_COLLAPSED : MAP_CARD_WIDTH}px;

  ${({ collapsed }) =>
    collapsed &&
    css`
      height: ${MAP_CARD_HEIGHT_COLLAPSED}px;
    `}
  .default-type-root-body {
    margin-bottom: -20px; // combat against extra margin from last child of default-type-object-children
  }
`;

export const StyledContentBox = styled(Box)`
  border: 1px solid ${({ theme }) => theme.colors.heavyLineOutline};
  border-radius: 5px;
  padding-top: 13px;
  ${StyledCard} {
    max-height: 600px;
  }
  ${({ isAviveRequest }) =>
    isAviveRequest &&
    css`
      margin-bottom: 10px;
    `}
`;

const MapCard = ({
  objectData,
  createdTime,
  layer,
  data,
  displaySchema,
  title = '',
  collapsed = false,
  renderCustom = () => null,
  isAviveRequest = false,
}) => {
  if (!displaySchema) {
    // Edge-case: When spatial objects arrive after expiring they are
    // temporarily delivered with a null value for attributes. This leads to
    // a blank InfoWindow on first hover, usually recovers by the second hover.
    // Investigate further, for now there's this guard clause.
    return <></>;
  }

  let patchedSchema = displaySchema;

  // Check if the given schema is a fragment or a full doc.
  if (displaySchema && !displaySchema.$schema) {
    // The schema is patched here to be a full JSON Schema document.
    let topLevelType = 'object';
    if (Array.isArray(displaySchema)) {
      topLevelType = 'array';
    }
    // A schema can be a top-level array or object.
    patchedSchema = {
      $schema: 'http://json-schema.org/draft-07/schema#',
      title,
      description: `Source: ${layer.source}`,
      ...(topLevelType === 'array'
        ? { items: { ...displaySchema } }
        : {
            propertiesOrder: [...Object.keys(displaySchema)],
            properties: { ...displaySchema },
          }),
    };
  }

  // The address location varies depending if the handling is coming from prague
  // vs hermes. Adding this in to be safe.
  let rgAddress = layer.rg_address || objectData.rg_address;
  let overridden = false;

  // If the data already has a reverse geocoded address, use that to override
  // the supplied rg_address. This is a case where partners choose to send their
  // own addresses, in which case they will be informed to send it as rg_address.
  if (objectData?.attributes?.rg_address) {
    rgAddress = objectData.attributes.rg_address;
    overridden = true;
  }

  const [longitude, latitude] =
    objectData.shape?.features[0]?.geometry?.coordinates ?? {};
  const coordinates = { longitude, latitude };

  let timestamp = objectData.date_created;
  // For snapshots, each individual object might have its own timestamp
  // when its data was last updated (date_pulled). When present, use it.
  if (objectData?.attributes?.date_pulled) {
    timestamp = objectData.attributes.date_pulled;
  }

  return (
    <StyledMapCard collapsed={collapsed}>
      <MapCardHeader
        data={data}
        displaySchema={displaySchema}
        layer={layer}
        timestamp={timestamp}
        title={title}
        collapsed={collapsed}
        wrapHeader={true}
      />
      {!collapsed && (
        <Scrollable>
          <>
            <Location
              location={rgAddress}
              overridden={overridden}
              coordinates={coordinates}
            />
            <StyledContentBox isAviveRequest={isAviveRequest}>
              <EllingtonCard
                data={data}
                display_schema={patchedSchema}
                createdTime={createdTime}
                shouldDisplayBlankFields
                borderless
                noHeader
                isAviveRequest={isAviveRequest}
              />
            </StyledContentBox>
          </>
          {renderCustom()}
        </Scrollable>
      )}
    </StyledMapCard>
  );
};

export default MapCard;
